import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IDBProduct, OnlineProduct } from '../interfaces/product';
import { catchError, timeout } from 'rxjs/operators';

/// OwnerNavCounts on the server (Databases/Services/Dashboard/OwnerDashboards.cs).
export interface OwnerNavCounts {
    OrdersNeedingAction: number;
    ProductsAwaitingApproval: number;
    ShopsAwaitingApproval: number;
    UnreadMessages: number;
    /// Conversations the shop takes part in, waiting on the owner (Messages.Conversations).
    UnreadConversations: number;
    /// Refunds of cancelled items owed and not sent (LineRefunds): the Payments page.
    RefundsOwed?: number;
    /// Sellers' pickup addresses waiting for the team's check: the Pickup addresses page.
    PickupsWaiting?: number;
}

@Injectable({
    providedIn: 'root',
})
export class NijederService {
    baseUrl: string = environment.apiUrl;

    constructor(private http: HttpClient) {}

    /// Platform-wide figures for the owner dashboard. Owner only - the server
    /// returns 403 for anyone whose SID is not 786.
    getOwnerDashboard(): Observable<any> {
        return this.http.get<any>(
            this.baseUrl + '/api/Publisher/GetOwnerDashboard'
        );
    }

    /// What waits for the owner, for the counts beside the account menu's links - the same
    /// figures as the dashboard's "Needs attention", four counts instead of the whole dashboard.
    getNavCounts(): Observable<OwnerNavCounts> {
        return this.http.get<OwnerNavCounts>(
            this.baseUrl + '/api/Publisher/GetNavCounts'
        );
    }

    /// IndexNow on this server: whether it announces here, how many urls are waiting,
    /// and how the last send went. Owner only.
    getIndexNowStatus(): Observable<any> {
        return this.http.get<any>(this.baseUrl + '/api/Seo/GetIndexNowStatus');
    }

    /// Text messages: the CloudWave wallet, what a text costs and how many are left, and today's phone codes
    /// (SmsController.Status). Owner only.
    getSmsStatus(): Observable<any> {
        return this.http.get<any>(this.baseUrl + '/api/Sms/Status');
    }

    /// Queues every url the sitemaps list, sent within two minutes. Owner only. Every
    /// publish, edit and delete announces itself already - this is for the first deploy,
    /// or changes made straight in the database.
    submitAllToIndexNow(): Observable<any> {
        return this.http.post<any>(this.baseUrl + '/api/Seo/SubmitAllToIndexNow', {});
    }

    /// Owner-only category administration. The server returns 403 for anyone whose
    /// SID is not 786.
    getCategoryTree(): Observable<any> {
        return this.http.get<any>(this.baseUrl + '/api/Category/GetCategoryTree');
    }

    /// Creates when model.ID is 0, updates otherwise. Resolves to
    /// { success, message, category }.
    saveCategory(model: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + '/api/Category/SaveCategory', model);
    }

    deleteCategory(id: number): Observable<any> {
        return this.http.post<any>(
            `${this.baseUrl}/api/Category/DeleteCategory/${id}`,
            {}
        );
    }

    /// Owner-only brand administration. Note these live under /api/Brand while the
    /// public brand reads stay on /api/Category - only the writes moved.
    getBrandList(filters: any = {}): Observable<any> {
        const params: string[] = [];

        // Filtering runs on the server, so the page stays usable once the table
        // holds thousands of imported rows.
        Object.keys(filters).forEach(key => {
            const value = filters[key];
            if (value !== null && value !== undefined && value !== '') {
                params.push(`${key}=${encodeURIComponent(value)}`);
            }
        });

        const query = params.length ? '?' + params.join('&') : '';
        return this.http.get<any>(`${this.baseUrl}/api/Brand/GetBrandList${query}`);
    }

    /// Categories, countries, brand types and badges for the editor's pickers.
    getBrandLookups(): Observable<any> {
        return this.http.get<any>(this.baseUrl + '/api/Brand/GetBrandLookups');
    }

    /// Creates when model.BrandId is 0, updates otherwise. Resolves to
    /// { success, message, brand }.
    saveBrand(model: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + '/api/Brand/SaveBrand', model);
    }

    deleteBrand(id: number): Observable<any> {
        return this.http.post<any>(
            `${this.baseUrl}/api/Brand/DeleteBrand/${id}`,
            {}
        );
    }

    /// Downloads a logo from `url` into the site's own filesystem and returns the
    /// stored urls, so a brand logo does not stay hosted somewhere else.
    fetchBrandLogo(payload: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + '/api/Brand/FetchBrandLogo', payload);
    }

    /// Uploads one category, brand or blog image from the admin's machine. `kind` is
    /// 'category', 'brand' or 'blog', `group` the subfolder (a brand type, 'categories', or
    /// for a blog 'covers' / 'content'), `name` the slug the filename is built from. Owner-only.
    ///
    /// No Content-Type header is set on purpose: the browser has to add the multipart
    /// boundary itself, and setting it by hand produces a request the server cannot
    /// parse.
    uploadAdminImage(file: File, kind: string, group: string, name: string): Observable<any> {
        const form = new FormData();
        form.append('files', file, file.name);
        form.append('kind', kind || 'category');
        form.append('group', group || '');
        form.append('name', name || '');

        return this.http.post<any>(this.baseUrl + '/api/Image/UploadAdminImage', form);
    }

    moveBrand(id: number, direction: 'up' | 'down'): Observable<any> {
        return this.http.post<any>(
            `${this.baseUrl}/api/Brand/MoveBrand/${id}/${direction}`,
            {}
        );
    }

    getAllPendingProducts(): Observable<IDBProduct[]> {
        return this.http.post<IDBProduct[]>(
            this.baseUrl + '/api/Publisher/GetAllPendingProducts',
            {}
        );
    }

    /// Sellers' products edited while their page was public, oldest first, with what kind of thing changed - the owner's
    /// second look (Product.LiveEditAt on the server).
    getLiveEdits(): Observable<any[]> {
        return this.http.get<any[]>(this.baseUrl + '/api/Publisher/GetLiveEdits');
    }

    /// The owner has looked at a seller's live edit: it leaves the list.
    /// fields: the changes the owner was shown - an edit made since is kept, and the answer says what it changed.
    liveEditSeen(productId: number, fields: string): Observable<any> {
        return this.http.post<any>(this.baseUrl + '/api/Publisher/LiveEditSeen', { ProdId: productId, Fields: fields || '' });
    }

    publishProduct(model: any): Observable<any> {
        const maxTimeout = 300000; // Maximum timeout in milliseconds (e.g., 5000ms = 5 seconds)
        return this.http
            .post<any>(`${this.baseUrl}/api/Publisher/PublishProduct`, model)
            .pipe(
                timeout(maxTimeout),
                catchError((error) => {
                    if (error.name === 'TimeoutError') {
                        console.error('Request timed out');
                    }
                    return throwError(() => error); // Re-throw the error
                })
            );
    }

    /// Opens (Active true) or closes a product's page - Product.IsActive, the inventory's Activate and Deactivate.
    /// Owner only. Closing it takes a product on the storefront offline too. Answers { Status, IsActive, IsPublic,
    /// ListingsRemoved }.
    setProductActive(model: { ProdId: number, SID: number, Active: boolean }): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/api/Publisher/SetProductActive`, { ProdId: model.ProdId, Sid: model.SID, Active: model.Active });
    }

    /// What the inventory holds for one product, for the owner's panel on the product page: { Product, Source: null }.
    getSourceForProduct(productId: number): Observable<any> {
        return this.http.get<any>(this.baseUrl + '/api/ProductPanel/ForProduct/' + productId);
    }

    /// A product's history, newest first: { Entries, More }. before = the ID of the last line shown.
    getProductHistory(productId: number, before: number = null): Observable<any> {
        return this.http.get<any>(this.baseUrl + '/api/ProductPanel/History/' + productId + (before ? '?before=' + before : ''));
    }

    // The owner's blog editor, /account/blog. Owner-only: 403 for anyone else.

    getBlogAdminList(): Observable<any[]> {
        return this.http.get<any[]>(this.baseUrl + '/api/Blog/AdminList');
    }

    getBlogAdmin(id: number): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/api/Blog/AdminGet/${id}`);
    }

    /// Creates the post when its ID is 0, otherwise changes it; `{ success, message, id, slug }`.
    saveBlog(model: any): Observable<any> {
        return this.http.post<any>(this.baseUrl + '/api/Blog/AdminSave', model);
    }

    deleteBlog(id: number): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/api/Blog/AdminDelete/${id}`, {});
    }

    /// The categories this site's posts may have ("buyers", "sellers", "stories"): the server's list, not one kept here.
    getBlogAdminCategories(): Observable<string[]> {
        return this.http.get<string[]>(this.baseUrl + '/api/Blog/AdminCategories');
    }

}
